import axios from 'axios';
import type {
  ApprovalRequestListItem,
  ApprovalRequestDetailResponse,
  ApprovalTaskItem,
  ApprovalFormItem,
  ApprovalSearchCondition,
  CreateApprovalRequest,
  ApprovalDecisionRequest,
  PageResponse,
} from '../types/approvalCenter';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// 인증 토큰 첨부 인터셉터
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ApiResponse<T> 언랩 인터셉터
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      '네트워크 오류';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

function buildSearchParams(params: ApprovalSearchCondition): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.formCode) sp.set('formCode', params.formCode);
  if (params.requester) sp.set('requester', params.requester);
  if (params.workflowName) sp.set('workflowName', params.workflowName);
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  if (params.keyword) sp.set('keyword', params.keyword);
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.size !== undefined) sp.set('size', String(params.size));
  if (params.sort) sp.set('sort', params.sort);
  return sp;
}

export const approvalCenterApi = {
  /** GET /api/eraf/approval-center/requests — 운영 목록 (APPROVAL_READ) */
  list: (params: ApprovalSearchCondition): Promise<PageResponse<ApprovalRequestListItem>> => {
    const sp = buildSearchParams(params);
    return apiClient
      .get<unknown, { data: PageResponse<ApprovalRequestListItem> }>(
        `/approval-center/requests?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** GET /api/eraf/approval-center/requests/{id} — 결재 상세 */
  get: (id: number): Promise<ApprovalRequestDetailResponse> =>
    apiClient
      .get<unknown, { data: ApprovalRequestDetailResponse }>(`/approval-center/requests/${id}`)
      .then((res) => res.data),

  /** GET /api/eraf/approval-center/requests/mine — 내가 올린 결재 */
  mine: (params: Omit<ApprovalSearchCondition, 'requester'>): Promise<PageResponse<ApprovalRequestListItem>> => {
    const sp = buildSearchParams(params as ApprovalSearchCondition);
    return apiClient
      .get<unknown, { data: PageResponse<ApprovalRequestListItem> }>(
        `/approval-center/requests/mine?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** GET /api/eraf/approval-center/tasks/mine — 받은 결재 (역할 기반 라우팅) */
  myTasks: (): Promise<ApprovalTaskItem[]> =>
    apiClient
      .get<unknown, { data: ApprovalTaskItem[] }>('/approval-center/tasks/mine')
      .then((res) => res.data),

  /** POST /api/eraf/approval-center/requests — 결재 상신 */
  create: (body: CreateApprovalRequest): Promise<ApprovalRequestDetailResponse> =>
    apiClient
      .post<unknown, { data: ApprovalRequestDetailResponse }>('/approval-center/requests', body)
      .then((res) => res.data),

  /** POST /api/eraf/approval-center/requests/{id}/approve — 승인 */
  approve: (id: number, body: ApprovalDecisionRequest): Promise<ApprovalRequestDetailResponse> =>
    apiClient
      .post<unknown, { data: ApprovalRequestDetailResponse }>(
        `/approval-center/requests/${id}/approve`,
        body
      )
      .then((res) => res.data),

  /** POST /api/eraf/approval-center/requests/{id}/reject — 반려 */
  reject: (id: number, body: ApprovalDecisionRequest): Promise<ApprovalRequestDetailResponse> =>
    apiClient
      .post<unknown, { data: ApprovalRequestDetailResponse }>(
        `/approval-center/requests/${id}/reject`,
        body
      )
      .then((res) => res.data),

  /** GET /api/eraf/approval-center/forms — 결재 양식 목록 */
  forms: (): Promise<ApprovalFormItem[]> =>
    apiClient
      .get<unknown, { data: ApprovalFormItem[] }>('/approval-center/forms')
      .then((res) => res.data),
};
